import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import Quill, { Delta } from 'quill/core';
import React, { useCallback, useEffect, useRef } from 'react';
import useIsLoggedIn from '../account/useIsLoggedIn';
import useVisitorSession from '../account/useVisitorSession';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { TextChangeHandler } from '../editor/Editor';
import Editor, { EditorContainer } from '../editor/Editor';
import QuillContext, { useQuill } from '../editor/QuillContext';
import WiderLineSizeContext from '../editor/WiderLineSizeContext';
import { EditorActions } from '../editor/editorSlice';
import EditorModal from '../editor/modals/EditorModal';
import NotFoundView from '../error/NotFoundView';
import { gql } from '../graphql/generated/gql';
import { PostLinkAccess } from '../graphql/generated/graphql';
import { UserRole } from '../topic/topicPolicyCheck';
import { UIActions } from '../ui/uiSlice';
import PageMeta from '../utils/PageMeta';
import useSlug from '../utils/router/useSlug';
import postPath from '../utils/url/postPath';
import usePublicNotice from '../utils/usePublicNotice';
import ControlBar, { FooterControlBar } from './ControlBar';
import HoverContentPreview from './HoverContentPreview';
import MagneticWall from './MagneticWall';
import * as PostLayout from './PostLayout';
import PostLayoutProvider from './PostLayoutProvider';
import PostSeriesBar from './PostSeriesBar';
import PostSkeleton from './PostSkeleton';
import PostStickyControl from './PostStickyControl';
import PostTOCProvider, { usePostTOC } from './PostTOCContext';
import PostViewBanner from './PostViewBanner';
import postPolicyCheck, { PostPolicyAction } from './postPolicyCheck';
import PostSeriesFooter from './series/PostSeriesFooter';
import PublicPostTopicBar from './topic/PublicPostTopicBar';
import useCachedPostTitle from './useCachedPostTitle';
import useHasMagneticWallItems from './useHasMagneticWallItems';
import useShareSecret from './useShareSecret';
import useAnchorScrollingHandler, {
  scrollAnchorIntoView,
} from '../editor/useAnchorScrollingHandler';

const PUBLIC_POST_QUERY = gql(`
  query PublicPostView($id: ID!, $shareSecret: String) {
    post: publicPost(id: $id, shareSecret: $shareSecret) {
      id
      linkAccess
      publishedAt
      content
      version
      banner {
        original
      }
      title
      series {
        id
        ...PostSeriesBar_series
        ...PostSeriesFooter_series
      }
      ...postPath_post
      ...postPolicyCheck_publicPost
      ...EditorActions_post
      ...Editor_post
      ...useHasMagneticWallItems_post
      ...PublicPostTopicBar_post
      ...usePublicNotice_post
    }
  }
`);

const CONTROL_BAR_POST_QUERY = gql(`
  query PublicPostViewControlBarPost($id: ID!) {
    post(id: $id) {
      id
      ...ControlBar_post
      ...FooterControlBar_post
    }
  }
`);

interface InnerPublicPostViewProps {
  postId: string;
}

const InnerPublicPostView = ({ postId }: InnerPublicPostViewProps) => {
  const { isTOCOpen } = usePostTOC();
  const { visitorSession, ensureVisitorSession } = useVisitorSession(postId);
  const isLoggedIn = useIsLoggedIn();

  const { quill } = useQuill();
  const { settings, name: organizationName } = window.__slab.PUBLIC_ORG;
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );

  const shareSecret = useShareSecret();
  const { data, loading } = useQuery(PUBLIC_POST_QUERY, {
    variables: { id: postId, shareSecret },
  });

  const { data: controlBarPostData } = useQuery(CONTROL_BAR_POST_QUERY, {
    variables: { id: postId },
    skip: !isLoggedIn,
  });

  const post = data?.post;

  const isEditable = !readOnly && !!visitorSession;
  const dispatch = useAppDispatch();

  const postLayoutContainerRef = useRef(null);
  const postBannerContainerRef = useRef<HTMLDivElement | null>(null);
  const cachedPostTitle = useCachedPostTitle(postId);

  useEffect(() => {
    if (!post) return undefined;
    dispatch(UIActions.setExternalTopbarLoginRedirectPath(postPath(post)));
    return () => {
      dispatch(UIActions.setExternalTopbarLoginRedirectPath());
    };
  }, [dispatch, post]);

  usePublicNotice(post);

  // Remote updates not applied yet
  const receivedRef = useRef(new Delta());

  const quillRef = useRef(quill);
  useEffect(() => {
    if (!quillRef.current && quill && document.location.hash) {
      scrollAnchorIntoView(quill, document.location.hash);
    }

    quillRef.current = quill;
    return () => {
      quillRef.current = null;
    };
  }, [quill]);

  useEffect(() => {
    const { hash } = document.location;
    if (hash) {
      const header = document.getElementById(hash.slice(1));
      if (header != null) {
        header.scrollIntoView();
      }
    }
  }, [post]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLImageElement;
    if (target.tagName === 'IMG') {
      dispatch(
        UIActions.openModal(EditorModal, { image: { src: target.src } }),
      );
    }
  };

  const isPostEditable =
    !!post &&
    !isLocked &&
    postPolicyCheck(PostPolicyAction.EditPost, post, UserRole.Anonymous);

  const loadedPostIdRef = useRef<string | null>(null);
  const hasMagneticWallItems = useHasMagneticWallItems(post) && isEditable;

  useEffect(() => {
    if (post && loadedPostIdRef.current !== post.id) {
      if (!isEditable) {
        dispatch(
          EditorActions.loadInitialContent({
            initialContent: post.content,
            initialVersion: post.version,
            editable: isPostEditable,
          }),
        );
        if (
          readOnly &&
          post &&
          !post.publishedAt &&
          post.linkAccess === PostLinkAccess.PublicEdit
        ) {
          dispatch(EditorActions.readOnly(false));
        }
      } else {
        dispatch(
          EditorActions.load({
            post,
            isEditable: isPostEditable,
            onRemoteDelta: (delta: Delta) => {
              receivedRef.current = receivedRef.current.compose(delta);
              quillRef.current?.updateContents(
                receivedRef.current,
                Quill.sources.API,
              );
            },
          }),
        );
        loadedPostIdRef.current = post.id;
      }
    }
  }, [dispatch, isEditable, isPostEditable, post, readOnly]);

  useEffect(
    () => () => {
      dispatch(EditorActions.leave());
    },
    [dispatch, postId],
  );

  useEffect(() => {
    if (!readOnly && isPostEditable && ensureVisitorSession) {
      ensureVisitorSession();
    }
  }, [ensureVisitorSession, isPostEditable, readOnly]);

  const anchorScrollingHandlerProps = useAnchorScrollingHandler(quill, postId, {
    updateLocationHash: true,
  });
  const handleTextChange: TextChangeHandler = useCallback(
    (_delta, _oldContent, source) => {
      if (source === Quill.sources.API) {
        receivedRef.current = new Delta();
      }
    },
    [],
  );

  if (!post && !loading) {
    return <NotFoundView />;
  }

  const currentBannerUrl = post?.banner?.original;
  const hasBanner = currentBannerUrl != null;
  const postTitle = post?.title || cachedPostTitle;

  return (
    <PostLayoutProvider post={post} hasMagneticWallItems={hasMagneticWallItems}>
      {postTitle && <PageMeta title={`${postTitle} - ${organizationName}`} />}
      <div
        id="content"
        className={classNames(
          'public-post post-container no-padding-x no-padding-y',
          { 'show-sticky-toc': isTOCOpen, 'has-banner': hasBanner },
        )}
        {...anchorScrollingHandlerProps}
      >
        <HoverContentPreview>
          <PostLayout.Container
            hasBanner={hasBanner}
            hasMagneticWallItems={hasMagneticWallItems}
            hasStateBar={false}
            ref={postLayoutContainerRef}
          >
            {post ? (
              <WiderLineSizeContext
                containerRef={postLayoutContainerRef}
                editor={quill ? quill.scroll.domNode : null}
              >
                {currentBannerUrl && (
                  <PostLayout.Banner ref={postBannerContainerRef}>
                    <PostViewBanner banner={currentBannerUrl} />
                  </PostLayout.Banner>
                )}
                <PostLayout.Above>
                  <PublicPostTopicBar post={post} />
                  {post.series && (
                    <PostSeriesBar postId={post.id} series={post.series} />
                  )}
                </PostLayout.Above>

                <PostLayout.Editor onDoubleClick={handleDoubleClick}>
                  <EditorContainer hasBanner={hasBanner}>
                    {controlBarPostData?.post && (
                      <PostLayout.ControlBar>
                        <ControlBar post={controlBarPostData.post} />
                      </PostLayout.ControlBar>
                    )}
                    <Editor
                      allowedEmbedHosts={settings.allowedEmbedHosts}
                      post={post}
                      namespace="publicEditor"
                      onTextChange={handleTextChange}
                    />
                  </EditorContainer>
                </PostLayout.Editor>

                <PostLayout.TOC>
                  <PostStickyControl
                    postId={post.id}
                    postLayoutBannerRef={postBannerContainerRef}
                    postLayoutContainerRef={postLayoutContainerRef}
                    hasBanner={hasBanner}
                  />
                </PostLayout.TOC>
                {isEditable && (
                  <PostLayout.MagneticWall>
                    {quill && (
                      <MagneticWall
                        quill={quill}
                        post={post}
                        canEditPost={isPostEditable}
                      />
                    )}
                  </PostLayout.MagneticWall>
                )}

                <PostLayout.Below>
                  {controlBarPostData?.post && (
                    <PostLayout.FooterControlBar>
                      <FooterControlBar post={controlBarPostData.post} />
                    </PostLayout.FooterControlBar>
                  )}
                  {post.series && (
                    <div>
                      <PostSeriesFooter postId={post.id} series={post.series} />
                      <PostSeriesBar postId={post.id} series={post.series} />
                    </div>
                  )}
                </PostLayout.Below>
              </WiderLineSizeContext>
            ) : (
              <PostLayout.Editor>
                <PostLayout.Above />
                <div className="post-layout-editor-inner">
                  <PostSkeleton />
                </div>
              </PostLayout.Editor>
            )}
          </PostLayout.Container>
        </HoverContentPreview>
      </div>
    </PostLayoutProvider>
  );
};

const PublicPostView = () => {
  const [postId] = useSlug('postSlug');

  return (
    <QuillContext>
      <PostTOCProvider>
        <InnerPublicPostView key={postId} postId={postId} />
      </PostTOCProvider>
    </QuillContext>
  );
};

export default PublicPostView;
